<script setup>
import { ref, computed, onMounted, reactive, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { cortexAgentsAPI, cortexToolsAPI } from 'dashboard/api/cortex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import NextCheckbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import CortexToolInlineForm from './CortexToolInlineForm.vue';

const props = defineProps({
  agent: { type: Object, default: null },
  knowledgeBases: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'created', 'updated']);
const { t } = useI18n();
const store = useStore();

// ═══════════ MODE ═══════════
const isEditMode = computed(() => !!props.agent);
const currentStep = ref(isEditMode.value ? 2 : 1);
const isCreating = ref(false);
const stepElements = ref({});

// ═══════════ TEMPLATE ICON MAP ═══════════
const TEMPLATE_ICONS = {
  support: 'i-lucide-headphones',
  sales: 'i-lucide-trending-up',
  finance: 'i-lucide-wallet',
  onboarding: 'i-lucide-rocket',
  customer_success: 'i-lucide-trophy',
  triage: 'i-lucide-git-branch',
};

const TEMPLATE_COLOR_CLASSES = {
  support: 'bg-n-brand/10 text-n-brand',
  sales: 'bg-n-brand/10 text-n-brand',
  finance: 'bg-n-brand/10 text-n-brand',
  onboarding: 'bg-n-brand/10 text-n-brand',
  customer_success: 'bg-n-brand/10 text-n-brand',
  triage: 'bg-n-slate-3 text-n-slate-11',
};
const templateColorClass = templateKey =>
  TEMPLATE_COLOR_CLASSES[templateKey] || TEMPLATE_COLOR_CLASSES.triage;

// ═══════════ STATE ═══════════
const templates = ref([]);
const selectedTemplate = ref(null);

const agentName = ref('');
const agentDescription = ref('');
const agentDefaultLocale = ref('pt_BR');
const agentAllowedLocales = ref(['pt_BR']);
const agentStatus = ref('testing');
const allowedKnowledgeBaseIds = ref([]);

const persona = reactive({
  tone: '',
  tone_traits: [],
  style: '',
  writing_preferences: '',
  writing_avoid: '',
  boundaries: '',
});

const instructions = reactive({
  mission: '',
  success_criteria: '',
});

const toneTraitOptions = computed(() => [
  {
    value: 'professional',
    label: t('CORTEX.AGENTS.WIZARD.TONE_PROFESSIONAL'),
    icon: 'i-lucide-briefcase-business',
  },
  {
    value: 'friendly',
    label: t('CORTEX.AGENTS.WIZARD.TONE_FRIENDLY'),
    icon: 'i-lucide-heart',
  },
  {
    value: 'informal',
    label: t('CORTEX.AGENTS.WIZARD.TONE_INFORMAL'),
    icon: 'i-lucide-smile',
  },
  {
    value: 'technical',
    label: t('CORTEX.AGENTS.WIZARD.TONE_TECHNICAL'),
    icon: 'i-lucide-glasses',
  },
  {
    value: 'empathetic',
    label: t('CORTEX.AGENTS.WIZARD.TONE_EMPATHETIC'),
    icon: 'i-lucide-hand-heart',
  },
  {
    value: 'direct',
    label: t('CORTEX.AGENTS.WIZARD.TONE_DIRECT'),
    icon: 'i-lucide-move-right',
  },
  {
    value: 'educational',
    label: t('CORTEX.AGENTS.WIZARD.TONE_EDUCATIONAL'),
    icon: 'i-lucide-graduation-cap',
  },
  {
    value: 'creative',
    label: t('CORTEX.AGENTS.WIZARD.TONE_CREATIVE'),
    icon: 'i-lucide-palette',
  },
]);
const selectedToneTraitLabels = computed(() =>
  toneTraitOptions.value
    .filter(option => persona.tone_traits.includes(option.value))
    .map(option => option.label)
);

const toggleToneTrait = toneTrait => {
  const index = persona.tone_traits.indexOf(toneTrait);
  if (index === -1) {
    persona.tone_traits.push(toneTrait);
  } else {
    persona.tone_traits.splice(index, 1);
  }
};

const handoffPolicy = reactive({
  mode: 'never',
  lgpd_refusal: 'pause',
});

const modelConfig = reactive({
  fallback_strategy: 'standard_failure',
  fallback_message: '',
  context_budget_priority: 'balanced',
  context_budget_tokens: 4000,
});

const runtimeSettings = reactive({
  autonomy_mode: 'supervised',
  contact_profile_consolidation_mode: 'deterministic',
  rolling_summary_message_threshold: 12,
  rolling_summary_frequency: 6,
});

const policySection = ref('operation');
const mediaAnalysisPolicy = reactive({
  image: 'automatic',
  video: 'automatic',
  document: 'automatic',
  audio: 'literal_transcription',
});

const safetyPolicy = reactive({
  allow_general_model_knowledge: false,
  require_source_for_factual_answers: true,
  sensitive_data_detection_enabled: true,
  sensitive_data_categories: [
    'cpf',
    'cnpj',
    'credit_card',
    'password',
    'jwt',
    'api_key',
    'serial',
  ],
  sensitive_data_action: 'confirm',
  prompt_injection_protection_enabled: true,
  blocked_topics: [],
  guardrail_default_action: 'block_respond',
});
const blockedTopicsText = ref('');

const wizardScenarios = ref([]);
const removedScenarioIds = ref([]);
const editingScenarioId = ref(null);
const newScenario = reactive({
  name: '',
  description: '',
  priority: 100,
  mission: '',
  matchAnyText: '',
});
const scenarioDraft = reactive({
  name: '',
  description: '',
  priority: 100,
  mission: '',
  status: 'draft',
  matchAnyText: '',
});

const allTools = ref([]);
const selectedToolIds = ref([]);
const originalToolIds = ref([]);
const showToolForm = ref(false);

const wizardInboxIds = ref([]);
const originalInboxIds = ref([]);
const wizardInboxSettings = reactive({});

const isActiveTool = tool => tool.status === 'active';
const activeTools = computed(() => allTools.value.filter(isActiveTool));
const selectedActiveToolIds = computed(() => {
  const activeToolIds = new Set(activeTools.value.map(tool => tool.id));
  return selectedToolIds.value.filter(toolId => activeToolIds.has(toolId));
});

// ═══════════ LOCALE OPTIONS ═══════════
const localeOptions = computed(() => [
  {
    value: 'pt_BR',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.pt_BR'
    ),
  },
  {
    value: 'en',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.en'
    ),
  },
  {
    value: 'es',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.es'
    ),
  },
  {
    value: 'de',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.de'
    ),
  },
  {
    value: 'fr',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.fr'
    ),
  },
  {
    value: 'it',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.it'
    ),
  },
  {
    value: 'pt',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.pt'
    ),
  },
]);

const selectedAgentAllowedLocales = computed({
  get: () => agentAllowedLocales.value,
  set: locales => {
    agentAllowedLocales.value = locales.length
      ? locales
      : [agentDefaultLocale.value || 'pt_BR'];
  },
});

const agentStatusOptions = computed(() => [
  { value: 'draft', label: t('CORTEX.AGENTS.STATUS.draft') },
  { value: 'testing', label: t('CORTEX.AGENTS.STATUS.testing') },
  { value: 'active', label: t('CORTEX.AGENTS.STATUS.active') },
  { value: 'paused', label: t('CORTEX.AGENTS.STATUS.paused') },
  { value: 'archived', label: t('CORTEX.AGENTS.STATUS.archived') },
]);

const toggleKnowledgeBase = knowledgeBaseId => {
  if (allowedKnowledgeBaseIds.value.includes(knowledgeBaseId)) {
    allowedKnowledgeBaseIds.value = allowedKnowledgeBaseIds.value.filter(
      id => id !== knowledgeBaseId
    );
  } else {
    allowedKnowledgeBaseIds.value = [
      ...allowedKnowledgeBaseIds.value,
      knowledgeBaseId,
    ];
  }
};

const normalizeList = value =>
  value
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);

// ═══════════ HANDOFF OPTIONS ═══════════
const handoffModeOptions = computed(() => [
  { value: 'never', label: t('CORTEX.AGENTS.WIZARD.HANDOFF_MODE_NEVER') },
  {
    value: 'on_request',
    label: t('CORTEX.AGENTS.WIZARD.HANDOFF_MODE_ON_REQUEST'),
  },
  {
    value: 'insufficient_knowledge',
    label: t('CORTEX.AGENTS.WIZARD.HANDOFF_MODE_INSUFFICIENT_KNOWLEDGE'),
  },
  {
    value: 'on_request_or_insufficient_knowledge',
    label: t(
      'CORTEX.AGENTS.WIZARD.HANDOFF_MODE_REQUEST_OR_INSUFFICIENT_KNOWLEDGE'
    ),
  },
]);

const handoffLgpdOptions = computed(() => [
  {
    value: 'pause',
    label: t('CORTEX.AGENTS.WIZARD.HANDOFF_LGPD_PAUSE'),
  },
  {
    value: 'handoff',
    label: t('CORTEX.AGENTS.WIZARD.HANDOFF_LGPD_HANDOFF'),
  },
  { value: 'close', label: t('CORTEX.AGENTS.WIZARD.HANDOFF_LGPD_CLOSE') },
]);

const fallbackStrategyOptions = computed(() => [
  {
    value: 'standard_failure',
    label: t('CORTEX.AGENTS.WIZARD.FALLBACK_STANDARD'),
    description: t('CORTEX.AGENTS.WIZARD.FALLBACK_STANDARD_HELP'),
  },
  {
    value: 'deterministic_fallback',
    label: t('CORTEX.AGENTS.WIZARD.FALLBACK_DETERMINISTIC'),
    description: t('CORTEX.AGENTS.WIZARD.FALLBACK_DETERMINISTIC_HELP'),
  },
  {
    value: 'immediate_handoff',
    label: t('CORTEX.AGENTS.WIZARD.FALLBACK_HANDOFF'),
    description: t('CORTEX.AGENTS.WIZARD.FALLBACK_HANDOFF_HELP'),
  },
]);

const contextBudgetOptions = computed(() => [
  {
    value: 'balanced',
    label: t('CORTEX.AGENTS.WIZARD.CONTEXT_BALANCED'),
    description: t('CORTEX.AGENTS.WIZARD.CONTEXT_BALANCED_HELP'),
  },
  {
    value: 'rag_first',
    label: t('CORTEX.AGENTS.WIZARD.CONTEXT_RAG_FIRST'),
    description: t('CORTEX.AGENTS.WIZARD.CONTEXT_RAG_FIRST_HELP'),
  },
  {
    value: 'customer_first',
    label: t('CORTEX.AGENTS.WIZARD.CONTEXT_CUSTOMER_FIRST'),
    description: t('CORTEX.AGENTS.WIZARD.CONTEXT_CUSTOMER_FIRST_HELP'),
  },
]);

const autonomyModeOptions = computed(() => [
  {
    value: 'supervised',
    label: t('CORTEX.AGENTS.WIZARD.AUTONOMY_SUPERVISED'),
    description: t('CORTEX.AGENTS.WIZARD.AUTONOMY_SUPERVISED_HELP'),
  },
  {
    value: 'total',
    label: t('CORTEX.AGENTS.WIZARD.AUTONOMY_TOTAL'),
    description: t('CORTEX.AGENTS.WIZARD.AUTONOMY_TOTAL_HELP'),
  },
  {
    value: 'review_required',
    label: t('CORTEX.AGENTS.WIZARD.AUTONOMY_REVIEW_REQUIRED'),
    description: t('CORTEX.AGENTS.WIZARD.AUTONOMY_REVIEW_REQUIRED_HELP'),
  },
]);

const imageAnalysisModeOptions = computed(() => [
  {
    value: 'automatic',
    label: t('CORTEX.AGENTS.WIZARD.IMAGE_ANALYSIS_AUTOMATIC'),
    description: t('CORTEX.AGENTS.WIZARD.IMAGE_ANALYSIS_AUTOMATIC_HELP'),
  },
  {
    value: 'ocr_only',
    label: t('CORTEX.AGENTS.WIZARD.IMAGE_ANALYSIS_OCR_ONLY'),
    description: t('CORTEX.AGENTS.WIZARD.IMAGE_ANALYSIS_OCR_ONLY_HELP'),
  },
  {
    value: 'visual',
    label: t('CORTEX.AGENTS.WIZARD.IMAGE_ANALYSIS_VISUAL'),
    description: t('CORTEX.AGENTS.WIZARD.IMAGE_ANALYSIS_VISUAL_HELP'),
  },
]);

const videoAnalysisModeOptions = computed(() => [
  {
    value: 'automatic',
    label: t('CORTEX.AGENTS.WIZARD.VIDEO_ANALYSIS_AUTOMATIC'),
    description: t('CORTEX.AGENTS.WIZARD.VIDEO_ANALYSIS_AUTOMATIC_HELP'),
  },
  {
    value: 'audio_ocr_only',
    label: t('CORTEX.AGENTS.WIZARD.VIDEO_ANALYSIS_AUDIO_OCR_ONLY'),
    description: t('CORTEX.AGENTS.WIZARD.VIDEO_ANALYSIS_AUDIO_OCR_ONLY_HELP'),
  },
  {
    value: 'visual',
    label: t('CORTEX.AGENTS.WIZARD.VIDEO_ANALYSIS_VISUAL'),
    description: t('CORTEX.AGENTS.WIZARD.VIDEO_ANALYSIS_VISUAL_HELP'),
  },
]);

const documentAnalysisModeOptions = computed(() => [
  {
    value: 'automatic',
    label: t('CORTEX.AGENTS.WIZARD.DOCUMENT_ANALYSIS_AUTOMATIC'),
    description: t('CORTEX.AGENTS.WIZARD.DOCUMENT_ANALYSIS_AUTOMATIC_HELP'),
  },
  {
    value: 'text_only',
    label: t('CORTEX.AGENTS.WIZARD.DOCUMENT_ANALYSIS_TEXT_ONLY'),
    description: t('CORTEX.AGENTS.WIZARD.DOCUMENT_ANALYSIS_TEXT_ONLY_HELP'),
  },
  {
    value: 'visual',
    label: t('CORTEX.AGENTS.WIZARD.DOCUMENT_ANALYSIS_VISUAL'),
    description: t('CORTEX.AGENTS.WIZARD.DOCUMENT_ANALYSIS_VISUAL_HELP'),
  },
]);

const audioAnalysisModeOptions = computed(() => [
  {
    value: 'literal_transcription',
    label: t('CORTEX.AGENTS.WIZARD.AUDIO_ANALYSIS_LITERAL'),
    description: t('CORTEX.AGENTS.WIZARD.AUDIO_ANALYSIS_LITERAL_HELP'),
  },
  {
    value: 'speaker_identification',
    label: t('CORTEX.AGENTS.WIZARD.AUDIO_ANALYSIS_SPEAKERS'),
    description: t('CORTEX.AGENTS.WIZARD.AUDIO_ANALYSIS_SPEAKERS_HELP'),
  },
  {
    value: 'disabled',
    label: t('CORTEX.AGENTS.WIZARD.AUDIO_ANALYSIS_DISABLED'),
    description: t('CORTEX.AGENTS.WIZARD.AUDIO_ANALYSIS_DISABLED_HELP'),
  },
]);

const mediaAnalysisFields = computed(() => [
  {
    key: 'image',
    label: t('CORTEX.AGENTS.WIZARD.MEDIA_TYPE_IMAGE'),
    options: imageAnalysisModeOptions.value,
  },
  {
    key: 'video',
    label: t('CORTEX.AGENTS.WIZARD.MEDIA_TYPE_VIDEO'),
    options: videoAnalysisModeOptions.value,
  },
  {
    key: 'document',
    label: t('CORTEX.AGENTS.WIZARD.MEDIA_TYPE_DOCUMENT'),
    options: documentAnalysisModeOptions.value,
  },
  {
    key: 'audio',
    label: t('CORTEX.AGENTS.WIZARD.MEDIA_TYPE_AUDIO'),
    options: audioAnalysisModeOptions.value,
  },
]);

const selectedOptionDescription = (options, value) =>
  options.find(option => option.value === value)?.description;

const mediaAnalysisDescription = field =>
  selectedOptionDescription(field.options, mediaAnalysisPolicy[field.key]);

const consolidationModeOptions = computed(() => [
  {
    value: 'deterministic',
    label: t('CORTEX.AGENTS.WIZARD.MEMORY_DETERMINISTIC'),
    description: t('CORTEX.AGENTS.WIZARD.MEMORY_DETERMINISTIC_HELP'),
  },
  {
    value: 'llm',
    label: t('CORTEX.AGENTS.WIZARD.MEMORY_LLM'),
    description: t('CORTEX.AGENTS.WIZARD.MEMORY_LLM_HELP'),
  },
]);

const guardrailActionOptions = computed(() => [
  {
    value: 'block_respond',
    label: t('CORTEX.AGENTS.WIZARD.GUARDRAIL_ACTION_BLOCK_RESPOND'),
  },
  {
    value: 'block_handoff',
    label: t('CORTEX.AGENTS.WIZARD.GUARDRAIL_ACTION_BLOCK_HANDOFF'),
  },
  {
    value: 'block_silent',
    label: t('CORTEX.AGENTS.WIZARD.GUARDRAIL_ACTION_BLOCK_SILENT'),
  },
]);

const sensitiveDataActionOptions = computed(() => [
  {
    value: 'confirm',
    label: t('CORTEX.AGENTS.WIZARD.GUARDRAIL_ACTION_CONFIRM'),
  },
  ...guardrailActionOptions.value,
]);

const sensitiveDataCategoryOptions = computed(() => [
  { value: 'cpf', label: t('CORTEX.AGENTS.WIZARD.SENSITIVE_CATEGORY_CPF') },
  { value: 'cnpj', label: t('CORTEX.AGENTS.WIZARD.SENSITIVE_CATEGORY_CNPJ') },
  {
    value: 'credit_card',
    label: t('CORTEX.AGENTS.WIZARD.SENSITIVE_CATEGORY_CREDIT_CARD'),
  },
  {
    value: 'password',
    label: t('CORTEX.AGENTS.WIZARD.SENSITIVE_CATEGORY_PASSWORD'),
  },
  { value: 'jwt', label: t('CORTEX.AGENTS.WIZARD.SENSITIVE_CATEGORY_JWT') },
  {
    value: 'api_key',
    label: t('CORTEX.AGENTS.WIZARD.SENSITIVE_CATEGORY_API_KEY'),
  },
  {
    value: 'serial',
    label: t('CORTEX.AGENTS.WIZARD.SENSITIVE_CATEGORY_SERIAL'),
  },
]);

const optionLabel = (options, value) =>
  options.find(option => option.value === value)?.label || value;

const handoffModeSummaryLabel = computed(() =>
  optionLabel(handoffModeOptions.value, handoffPolicy.mode)
);

const handoffLgpdSummaryLabel = computed(() =>
  optionLabel(handoffLgpdOptions.value, handoffPolicy.lgpd_refusal)
);

// ═══════════ INBOXES ═══════════
const allInboxes = computed(() => store.getters['inboxes/getInboxes']);
const selectedWizardInboxes = computed(() =>
  (allInboxes.value || []).filter(inbox =>
    wizardInboxIds.value.includes(inbox.id)
  )
);

const ensureInboxSettings = inboxId => {
  if (!wizardInboxSettings[inboxId]) {
    wizardInboxSettings[inboxId] = {
      operational_mode: 'observe_only',
      cortex_chat_external_visual_enabled: false,
    };
  }
  return wizardInboxSettings[inboxId];
};

const toggleWizardInbox = inboxId => {
  const idx = wizardInboxIds.value.indexOf(inboxId);
  if (idx === -1) {
    wizardInboxIds.value.push(inboxId);
    ensureInboxSettings(inboxId);
  } else {
    wizardInboxIds.value.splice(idx, 1);
  }
};

// ═══════════ STEPS ═══════════
const STEPS = [
  { num: 1, label: t('CORTEX.AGENTS.WIZARD.STEP_TEMPLATE') },
  { num: 2, label: t('CORTEX.AGENTS.WIZARD.STEP_IDENTITY') },
  { num: 3, label: t('CORTEX.AGENTS.WIZARD.STEP_PERSONA') },
  { num: 4, label: t('CORTEX.AGENTS.WIZARD.STEP_INSTRUCTIONS') },
  { num: 5, label: t('CORTEX.AGENTS.WIZARD.STEP_POLICIES') },
  { num: 6, label: t('CORTEX.AGENTS.WIZARD.STEP_SCENARIOS') },
  { num: 7, label: t('CORTEX.AGENTS.WIZARD.STEP_TOOLS') },
  { num: 8, label: t('CORTEX.AGENTS.WIZARD.STEP_INBOXES') },
  { num: 9, label: t('CORTEX.AGENTS.WIZARD.STEP_CONFIRM') },
];

const visibleSteps = computed(() =>
  isEditMode.value ? STEPS.filter(s => s.num > 1) : STEPS
);

const setStepElement = (stepNumber, element) => {
  if (element) {
    stepElements.value[stepNumber] = element;
  } else {
    delete stepElements.value[stepNumber];
  }
};

const scrollActiveStepIntoView = async () => {
  await nextTick();
  const activeStepElement = stepElements.value[currentStep.value];
  if (!activeStepElement?.scrollIntoView) return;

  activeStepElement.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'center',
  });
};

const canNavigateToStep = stepNumber => stepNumber <= currentStep.value;

const goToStep = stepNumber => {
  if (canNavigateToStep(stepNumber)) {
    currentStep.value = stepNumber;
  }
};

watch(currentStep, scrollActiveStepIntoView, { immediate: true });

// ═══════════ TEMPLATE SELECTION ═══════════
const selectTemplate = tmpl => {
  selectedTemplate.value = tmpl;
  agentName.value = tmpl.name;
  agentDescription.value = tmpl.description || '';

  persona.tone = tmpl.persona?.tone || '';
  persona.tone_traits = tmpl.persona?.tone_traits || [];
  persona.style = tmpl.persona?.style || '';
  persona.writing_preferences = tmpl.persona?.writing_preferences || '';
  persona.writing_avoid = tmpl.persona?.writing_avoid || '';
  persona.boundaries = tmpl.persona?.boundaries || '';

  instructions.mission = tmpl.instructions?.mission || '';
  instructions.success_criteria = tmpl.instructions?.success_criteria || '';

  if (tmpl.handoff_policy) {
    handoffPolicy.mode = tmpl.handoff_policy.mode || 'never';
    handoffPolicy.lgpd_refusal = tmpl.handoff_policy.lgpd_refusal || 'pause';
  }

  if (tmpl.safety_policy) {
    safetyPolicy.allow_general_model_knowledge =
      tmpl.safety_policy.allow_general_model_knowledge ?? false;
    safetyPolicy.require_source_for_factual_answers =
      tmpl.safety_policy.require_source_for_factual_answers ?? true;
    safetyPolicy.sensitive_data_detection_enabled =
      tmpl.safety_policy.sensitive_data_detection_enabled ?? true;
    safetyPolicy.sensitive_data_categories =
      tmpl.safety_policy.sensitive_data_categories ||
      safetyPolicy.sensitive_data_categories;
    safetyPolicy.sensitive_data_action =
      tmpl.safety_policy.sensitive_data_action || 'confirm';
    safetyPolicy.prompt_injection_protection_enabled =
      tmpl.safety_policy.prompt_injection_protection_enabled ?? true;
    safetyPolicy.blocked_topics = tmpl.safety_policy.blocked_topics || [];
    safetyPolicy.guardrail_default_action =
      tmpl.safety_policy.guardrail_default_action || 'block_respond';
    blockedTopicsText.value = safetyPolicy.blocked_topics.join('\n');
  }

  currentStep.value = 2;
};

// ═══════════ SCENARIOS ═══════════
const addScenario = () => {
  if (!newScenario.name.trim()) return;
  wizardScenarios.value.push({
    id: Date.now(),
    name: newScenario.name.trim(),
    description: newScenario.description.trim(),
    priority: newScenario.priority,
    mission: newScenario.mission.trim(),
    status: 'draft',
    conditions: { match_any: normalizeList(newScenario.matchAnyText) },
  });
  newScenario.name = '';
  newScenario.description = '';
  newScenario.priority = 100;
  newScenario.mission = '';
  newScenario.matchAnyText = '';
};

const removeScenario = idx => {
  const [scenario] = wizardScenarios.value.splice(idx, 1);
  if (scenario.existingId) removedScenarioIds.value.push(scenario.existingId);
  if (editingScenarioId.value === scenario.id) editingScenarioId.value = null;
};

const editScenario = scenario => {
  editingScenarioId.value = scenario.id;
  Object.assign(scenarioDraft, {
    name: scenario.name,
    description: scenario.description,
    priority: scenario.priority,
    mission: scenario.mission,
    status: scenario.status,
    matchAnyText: (scenario.conditions?.match_any || []).join('\n'),
  });
};

const cancelScenarioEdit = () => {
  editingScenarioId.value = null;
};

const saveScenarioEdit = scenario => {
  if (!scenarioDraft.name.trim()) return;
  Object.assign(scenario, {
    name: scenarioDraft.name.trim(),
    description: scenarioDraft.description.trim(),
    priority: scenarioDraft.priority,
    mission: scenarioDraft.mission.trim(),
    status: scenarioDraft.status,
    conditions: {
      ...(scenario.conditions || {}),
      match_any: normalizeList(scenarioDraft.matchAnyText),
    },
  });
  editingScenarioId.value = null;
};

const scenarioStatusMeta = scenario => {
  if (scenario.status === 'active') {
    return {
      label: t('CORTEX.AGENTS.WIZARD.SCENARIO_STATUS_ACTIVE'),
      actionLabel: t('CORTEX.AGENTS.WIZARD.SCENARIO_DEACTIVATE'),
      icon: 'i-lucide-circle-check',
      className: 'text-n-brand bg-n-brand/10',
    };
  }
  if (scenario.status === 'paused') {
    return {
      label: t('CORTEX.AGENTS.WIZARD.SCENARIO_STATUS_INACTIVE'),
      actionLabel: t('CORTEX.AGENTS.WIZARD.SCENARIO_ACTIVATE'),
      icon: 'i-lucide-circle-pause',
      className: 'text-n-slate-11 bg-n-alpha-2',
    };
  }
  return {
    label: t('CORTEX.AGENTS.WIZARD.SCENARIO_STATUS_DRAFT'),
    actionLabel: t('CORTEX.AGENTS.WIZARD.SCENARIO_ACTIVATE'),
    icon: 'i-lucide-circle-dashed',
    className: 'text-n-amber-11 bg-n-amber-3',
  };
};

const toggleScenarioStatus = scenario => {
  if (scenario.status === 'active') {
    scenario.status = 'paused';
    return;
  }
  if (!(scenario.conditions?.match_any || []).length) {
    useAlert(t('CORTEX.AGENTS.WIZARD.SCENARIO_CRITERIA_REQUIRED'));
    editScenario(scenario);
    return;
  }
  scenario.status = 'active';
};

// ═══════════ TOOLS ═══════════
const toggleTool = toolId => {
  const idx = selectedToolIds.value.indexOf(toolId);
  if (idx === -1) {
    selectedToolIds.value.push(toolId);
  } else {
    selectedToolIds.value.splice(idx, 1);
  }
};

const handleToolCreated = tool => {
  allTools.value.push(tool);
  selectedToolIds.value.push(tool.id);
  showToolForm.value = false;
};

const isConversionSystemTool = tool => tool.config?.conversion_tool === true;

const selectDefaultConversionTools = () => {
  selectedToolIds.value = activeTools.value
    .filter(isConversionSystemTool)
    .map(tool => tool.id);
};

// ═══════════ VALIDATION ═══════════
const canAdvance = computed(() => {
  if (currentStep.value === 2) return agentName.value.trim().length > 0;
  if (currentStep.value === 3)
    return (
      (persona.tone_traits.length > 0 || persona.tone.trim().length > 0) &&
      persona.style.trim().length > 0
    );
  if (currentStep.value === 4) return instructions.mission.trim().length > 0;
  return true;
});

const canCreate = computed(
  () => agentName.value.trim() && instructions.mission.trim()
);

// ═══════════ NAVIGATION ═══════════
const goBack = () => {
  if (currentStep.value > (isEditMode.value ? 2 : 1)) {
    currentStep.value -= 1;
  } else {
    emit('close');
  }
};

const goNext = () => {
  if (canAdvance.value && currentStep.value < 9) {
    currentStep.value += 1;
  }
};

// ═══════════ EDIT MODE: Load existing data ═══════════
onMounted(async () => {
  try {
    const { data: templatesData } = await cortexAgentsAPI.templates();
    templates.value = templatesData || [];
  } catch {
    // Templates load silently; not blocking
  }

  try {
    const { data: toolsData } = await cortexToolsAPI.get();
    allTools.value = toolsData?.payload || toolsData || [];
    if (!isEditMode.value) {
      selectDefaultConversionTools();
    }
  } catch {
    // Tools load silently
  }

  if (!isEditMode.value) return;

  const agent = props.agent;
  agentName.value = agent.name || '';
  agentDescription.value = agent.description || '';
  agentStatus.value = agent.status || 'testing';
  allowedKnowledgeBaseIds.value =
    agent.settings?.allowed_knowledge_base_ids?.map(Number) || [];
  agentDefaultLocale.value = agent.default_locale || 'pt_BR';
  agentAllowedLocales.value = agent.allowed_locales || ['pt_BR'];
  selectedTemplate.value =
    templates.value.find(template => template.key === agent.template_key) ||
    null;

  persona.tone = agent.persona?.tone || '';
  persona.tone_traits = agent.persona?.tone_traits || [];
  persona.style = agent.persona?.style || '';
  persona.writing_preferences = agent.persona?.writing_preferences || '';
  persona.writing_avoid = agent.persona?.writing_avoid || '';
  persona.boundaries = agent.persona?.boundaries || '';

  instructions.mission = agent.instructions?.mission || '';
  instructions.success_criteria = agent.instructions?.success_criteria || '';

  handoffPolicy.mode = agent.handoff_policy?.mode || 'never';
  handoffPolicy.lgpd_refusal = agent.handoff_policy?.lgpd_refusal || 'pause';

  safetyPolicy.allow_general_model_knowledge =
    agent.safety_policy?.allow_general_model_knowledge ?? false;
  safetyPolicy.require_source_for_factual_answers =
    agent.safety_policy?.require_source_for_factual_answers ?? true;
  safetyPolicy.sensitive_data_detection_enabled =
    agent.safety_policy?.sensitive_data_detection_enabled ?? true;
  safetyPolicy.sensitive_data_categories =
    agent.safety_policy?.sensitive_data_categories ||
    safetyPolicy.sensitive_data_categories;
  safetyPolicy.sensitive_data_action =
    agent.safety_policy?.sensitive_data_action || 'confirm';
  safetyPolicy.prompt_injection_protection_enabled =
    agent.safety_policy?.prompt_injection_protection_enabled ?? true;
  safetyPolicy.blocked_topics = agent.safety_policy?.blocked_topics || [];
  safetyPolicy.guardrail_default_action =
    agent.safety_policy?.guardrail_default_action || 'block_respond';
  blockedTopicsText.value = safetyPolicy.blocked_topics.join('\n');

  modelConfig.fallback_strategy =
    agent.model_config?.fallback_strategy || 'standard_failure';
  modelConfig.fallback_message = agent.model_config?.fallback_message || '';
  modelConfig.context_budget_priority =
    agent.model_config?.context_budget_priority || 'balanced';
  modelConfig.context_budget_tokens =
    agent.model_config?.context_budget_tokens || 4000;
  Object.assign(runtimeSettings, {
    ...runtimeSettings,
    autonomy_mode: agent.settings?.autonomy_mode || 'supervised',
    contact_profile_consolidation_mode:
      agent.settings?.contact_profile_consolidation_mode || 'deterministic',
    rolling_summary_message_threshold:
      agent.settings?.rolling_summary_message_threshold || 12,
    rolling_summary_frequency: agent.settings?.rolling_summary_frequency || 6,
  });
  Object.assign(mediaAnalysisPolicy, {
    image: agent.settings?.media_analysis_policy?.image || 'automatic',
    video: agent.settings?.media_analysis_policy?.video || 'automatic',
    document: agent.settings?.media_analysis_policy?.document || 'automatic',
    audio:
      agent.settings?.media_analysis_policy?.audio || 'literal_transcription',
  });

  try {
    const { data: scenariosData } = await cortexAgentsAPI.scenarios(agent.id);
    wizardScenarios.value = (scenariosData || []).map(s => ({
      id: s.id,
      existingId: s.id,
      name: s.name,
      description: s.description || '',
      priority: s.priority ?? 100,
      mission: s.instructions?.mission || '',
      status: s.status || 'draft',
      conditions: s.conditions || {},
    }));
  } catch {
    // Scenarios load silently
  }

  try {
    const { data: agentToolsData } = await cortexAgentsAPI.tools(agent.id);
    const agentTools = agentToolsData || [];
    selectedToolIds.value = agentTools.map(p => p.cortex_tool_id || p.tool?.id);
    originalToolIds.value = [...selectedToolIds.value];
  } catch {
    // Tools load silently
  }

  try {
    const { data: agentInboxesData } = await cortexAgentsAPI.inboxes(agent.id);
    const agentInboxes = agentInboxesData?.payload || agentInboxesData || [];
    wizardInboxIds.value = agentInboxes.map(link => link.inbox_id);
    originalInboxIds.value = [...wizardInboxIds.value];
    agentInboxes.forEach(link => {
      wizardInboxSettings[link.inbox_id] = {
        operational_mode: link.settings?.operational_mode || 'respond',
        cortex_chat_external_visual_enabled:
          link.settings?.cortex_chat_external_visual_enabled === true,
      };
    });
  } catch {
    // Inboxes load silently
  }
});

// ═══════════ CREATE / UPDATE ═══════════
const buildAgentPayload = () => {
  const existingSettings = { ...(props.agent?.settings || {}) };

  return {
    name: agentName.value.trim(),
    description: agentDescription.value.trim(),
    status: agentStatus.value,
    default_locale: agentDefaultLocale.value,
    allowed_locales: agentAllowedLocales.value,
    settings: {
      ...existingSettings,
      allowed_knowledge_base_ids: allowedKnowledgeBaseIds.value,
      ...runtimeSettings,
      media_analysis_policy: { ...mediaAnalysisPolicy },
      rolling_summary_message_threshold: Number(
        runtimeSettings.rolling_summary_message_threshold
      ),
      rolling_summary_frequency: Number(
        runtimeSettings.rolling_summary_frequency
      ),
    },
    model_config: {
      fallback_strategy: modelConfig.fallback_strategy,
      fallback_message: modelConfig.fallback_message,
      context_budget_priority: modelConfig.context_budget_priority,
      context_budget_tokens: Number(modelConfig.context_budget_tokens),
    },
    persona: { ...persona },
    instructions: { ...instructions },
    handoff_policy: { ...handoffPolicy },
    safety_policy: {
      ...safetyPolicy,
      blocked_topics: normalizeList(blockedTopicsText.value),
    },
  };
};

const diffIds = (sourceIds, targetIds) => {
  const targetSet = new Set(targetIds.map(id => String(id)));
  return sourceIds.filter(id => !targetSet.has(String(id)));
};

const createOrUpdateAgent = async () => {
  if (!canCreate.value || isCreating.value) return;
  isCreating.value = true;

  try {
    let agentId;

    if (isEditMode.value) {
      await cortexAgentsAPI.update(props.agent.id, {
        agent: buildAgentPayload(),
      });
      agentId = props.agent.id;

      // Sync scenarios: create new, update existing
      await Promise.all(
        wizardScenarios.value.map(scenario => {
          if (scenario.existingId) {
            return cortexAgentsAPI.updateScenario(
              agentId,
              scenario.existingId,
              {
                scenario: {
                  name: scenario.name,
                  description: scenario.description,
                  priority: scenario.priority,
                  instructions: { mission: scenario.mission },
                  status: scenario.status || 'draft',
                  conditions: scenario.conditions || { match_any: [] },
                },
              }
            );
          }
          return cortexAgentsAPI.createScenario(agentId, {
            scenario: {
              name: scenario.name,
              description: scenario.description,
              priority: scenario.priority,
              instructions: { mission: scenario.mission },
              status: scenario.status || 'draft',
              conditions: scenario.conditions || { match_any: [] },
            },
          });
        })
      );

      const toolsToConnect = diffIds(
        selectedToolIds.value,
        originalToolIds.value
      );
      const toolsToDisconnect = diffIds(
        originalToolIds.value,
        selectedToolIds.value
      );
      const inboxesToConnect = diffIds(
        wizardInboxIds.value,
        originalInboxIds.value
      );
      const inboxesToDisconnect = diffIds(
        originalInboxIds.value,
        wizardInboxIds.value
      );

      await Promise.all([
        ...removedScenarioIds.value.map(scenarioId =>
          cortexAgentsAPI.deleteScenario(agentId, scenarioId)
        ),
        ...toolsToConnect.map(toolId =>
          cortexAgentsAPI.connectTool(agentId, { tool_id: toolId })
        ),
        ...toolsToDisconnect.map(toolId =>
          cortexAgentsAPI.disconnectTool(agentId, toolId)
        ),
        ...inboxesToConnect.map(inboxId =>
          cortexAgentsAPI.connectInbox(agentId, {
            inbox_id: inboxId,
            settings: ensureInboxSettings(inboxId),
          })
        ),
        ...wizardInboxIds.value
          .filter(inboxId => originalInboxIds.value.includes(inboxId))
          .map(inboxId =>
            cortexAgentsAPI.updateInbox(agentId, inboxId, {
              settings: ensureInboxSettings(inboxId),
            })
          ),
        ...inboxesToDisconnect.map(inboxId =>
          cortexAgentsAPI.disconnectInbox(agentId, inboxId)
        ),
      ]);

      useAlert(t('CORTEX.AGENTS.API.UPDATED'));
      emit('updated', agentId);
    } else {
      const wizardPayload = { agent: buildAgentPayload() };
      if (selectedTemplate.value?.key) {
        wizardPayload.template_key = selectedTemplate.value.key;
      }
      const { data } = await cortexAgentsAPI.wizard(wizardPayload);
      agentId = data.id;

      // Create scenarios
      await Promise.all(
        wizardScenarios.value.map(scenario =>
          cortexAgentsAPI.createScenario(agentId, {
            scenario: {
              name: scenario.name,
              description: scenario.description,
              priority: scenario.priority,
              instructions: { mission: scenario.mission },
              status: scenario.status || 'draft',
              conditions: scenario.conditions || { match_any: [] },
            },
          })
        )
      );

      // Connect tools
      await Promise.all(
        selectedToolIds.value.map(toolId =>
          cortexAgentsAPI.connectTool(agentId, { tool_id: toolId })
        )
      );

      // Connect inboxes
      await Promise.all(
        wizardInboxIds.value.map(inboxId =>
          cortexAgentsAPI.connectInbox(agentId, {
            inbox_id: inboxId,
            settings: ensureInboxSettings(inboxId),
          })
        )
      );

      useAlert(t('CORTEX.AGENTS.API.CREATED'));
      emit('created', agentId);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Wizard save failed:', err);
    useAlert(
      isEditMode.value
        ? t('CORTEX.AGENTS.API.UPDATE_ERROR')
        : t('CORTEX.AGENTS.API.ERROR')
    );
  } finally {
    isCreating.value = false;
  }
};

// ═══════════ HELPERS ═══════════
const riskBadgeColor = level => {
  const map = {
    low: 'teal',
    medium: 'amber',
    high: 'ruby',
    critical: 'ruby',
  };
  return map[level] || 'slate';
};
const riskBadgeLabel = level =>
  ({
    low: t('CORTEX.TOOLS.RISK.low'),
    medium: t('CORTEX.TOOLS.RISK.medium'),
    high: t('CORTEX.TOOLS.RISK.high'),
    critical: t('CORTEX.TOOLS.RISK.critical'),
  })[level] || level;

const toggleSensitiveDataCategory = category => {
  const idx = safetyPolicy.sensitive_data_categories.indexOf(category);
  if (idx === -1) {
    safetyPolicy.sensitive_data_categories.push(category);
  } else {
    safetyPolicy.sensitive_data_categories.splice(idx, 1);
  }
};

defineExpose({
  currentStep,
  agentName,
  persona,
  instructions,
  handoffPolicy,
  modelConfig,
  runtimeSettings,
  mediaAnalysisPolicy,
  safetyPolicy,
  wizardScenarios,
  newScenario,
  scenarioDraft,
  selectedToolIds,
  wizardInboxIds,
  selectedTemplate,
  toneTraitOptions,
  blockedTopicsText,
  canAdvance,
  canCreate,
  isCreating,
  selectTemplate,
  addScenario,
  removeScenario,
  editScenario,
  cancelScenarioEdit,
  saveScenarioEdit,
  toggleScenarioStatus,
  scenarioStatusMeta,
  toggleToneTrait,
  toggleSensitiveDataCategory,
  createOrUpdateAgent,
  goNext,
  goBack,
  goToStep,
});
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black1 backdrop-blur-sm"
    >
      <div
        data-testid="cortex-agent-wizard-dialog"
        class="bg-n-surface-1 rounded-lg shadow-xl w-full max-w-4xl h-[92vh] sm:h-[80vh] sm:max-h-[52rem] mx-4 border border-n-weak overflow-hidden flex flex-col"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak shrink-0"
        >
          <div class="flex items-center gap-3">
            <span
              :class="isEditMode ? 'i-lucide-pencil' : 'i-lucide-wand-sparkles'"
              class="size-5 text-n-brand"
            />
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{
                isEditMode
                  ? t('CORTEX.AGENTS.WIZARD.TITLE_EDIT')
                  : t('CORTEX.AGENTS.WIZARD.TITLE')
              }}
            </h2>
          </div>
          <button
            class="p-1.5 rounded-lg hover:bg-n-alpha-2"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-5 text-n-slate-11" />
          </button>
        </div>

        <!-- Progress Bar -->
        <div
          data-testid="cortex-agent-wizard-mobile-progress"
          class="sm:hidden flex items-center gap-3 px-5 py-3 border-b border-n-weak/50 shrink-0"
        >
          <span
            class="flex items-center justify-center size-7 rounded-full bg-n-brand text-white text-xs font-bold"
          >
            {{ currentStep }}
          </span>
          <div class="min-w-0">
            <p class="text-xs text-n-slate-11">
              {{
                t('CORTEX.AGENTS.WIZARD.STEP_PROGRESS', {
                  current: currentStep,
                  total: visibleSteps.length,
                })
              }}
            </p>
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ visibleSteps.find(step => step.num === currentStep)?.label }}
            </p>
          </div>
        </div>
        <div
          class="hidden sm:flex items-center gap-1 px-6 py-3 border-b border-n-weak/50 shrink-0 overflow-x-auto no-scrollbar"
        >
          <template v-for="(step, idx) in visibleSteps" :key="step.num">
            <button
              :ref="element => setStepElement(step.num, element)"
              type="button"
              data-testid="cortex-agent-wizard-step"
              :data-step-number="step.num"
              :disabled="!canNavigateToStep(step.num)"
              class="flex items-center gap-1.5 text-xs font-medium transition-colors shrink-0 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:cursor-default"
              :class="
                currentStep >= step.num
                  ? 'text-n-brand hover:text-n-brand/80 cursor-pointer'
                  : 'text-n-slate-10'
              "
              @click="goToStep(step.num)"
            >
              <span
                class="flex items-center justify-center size-6 rounded-full text-[10px] font-bold transition-all"
                :class="
                  currentStep >= step.num
                    ? 'bg-n-brand text-white'
                    : 'bg-n-alpha-2 text-n-slate-10'
                "
              >
                <span
                  v-if="currentStep > step.num"
                  class="i-lucide-check size-3.5"
                />
                <template v-else>{{ step.num }}</template>
              </span>
              <span class="hidden sm:inline whitespace-nowrap">{{
                step.label
              }}</span>
            </button>
            <span
              v-if="idx < visibleSteps.length - 1"
              class="flex-1 h-px bg-n-weak mx-1 min-w-2"
            />
          </template>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto px-5 sm:px-6 py-5 pb-10">
          <!-- ═══ STEP 1: Template Selection ═══ -->
          <div v-if="currentStep === 1">
            <p class="text-sm text-n-slate-11 mb-4">
              {{ t('CORTEX.AGENTS.WIZARD.STEP1_DESC') }}
            </p>
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <button
                v-for="tmpl in templates"
                :key="tmpl.key"
                type="button"
                class="group flex flex-col items-center gap-2.5 p-4 rounded-lg border border-n-weak hover:border-n-brand/50 hover:bg-n-brand/5 transition-colors text-center"
                @click="selectTemplate(tmpl)"
              >
                <div
                  class="flex items-center justify-center size-12 rounded-lg"
                  :class="templateColorClass(tmpl.key)"
                >
                  <span
                    :class="TEMPLATE_ICONS[tmpl.key] || 'i-lucide-bot'"
                    class="size-6"
                  />
                </div>
                <span class="text-sm font-medium text-n-slate-12">
                  {{ tmpl.name }}
                </span>
                <span class="text-[11px] text-n-slate-11 leading-tight">
                  {{ tmpl.description }}
                </span>
              </button>
            </div>
          </div>

          <!-- ═══ STEP 2: Identity ═══ -->
          <div v-if="currentStep === 2">
            <p class="text-sm text-n-slate-11 mb-4">
              {{ t('CORTEX.AGENTS.WIZARD.STEP2_DESC') }}
            </p>
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('CORTEX.AGENTS.WIZARD.IDENTITY_NAME') }}
                </label>
                <NextInput
                  v-model="agentName"
                  class="w-full"
                  :message="t('CORTEX.AGENTS.WIZARD.IDENTITY_NAME_HELP')"
                  :placeholder="
                    t('CORTEX.AGENTS.WIZARD.IDENTITY_NAME_PLACEHOLDER')
                  "
                  required
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('CORTEX.AGENTS.WIZARD.IDENTITY_DESCRIPTION') }}
                </label>
                <TextArea
                  v-model="agentDescription"
                  auto-height
                  min-height="5rem"
                  max-height="16rem"
                  aria-describedby="agent-objective-help"
                  :placeholder="
                    t('CORTEX.AGENTS.WIZARD.IDENTITY_DESCRIPTION_PLACEHOLDER')
                  "
                />
                <p
                  id="agent-objective-help"
                  class="mt-1.5 text-xs text-n-slate-11"
                >
                  {{ t('CORTEX.AGENTS.WIZARD.IDENTITY_DESCRIPTION_HELP') }}
                </p>
              </div>
              <div
                data-testid="cortex-agent-language-fields"
                class="grid items-start grid-cols-1 gap-4 sm:grid-cols-2"
              >
                <div class="flex min-w-0 flex-col">
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('CORTEX.AGENTS.WIZARD.IDENTITY_LOCALE') }}
                  </label>
                  <AdaptiveSelect
                    v-model="agentDefaultLocale"
                    :options="localeOptions"
                    :message="t('CORTEX.AGENTS.WIZARD.IDENTITY_LOCALE_HELP')"
                    message-class="sm:min-h-8"
                    class="w-full"
                  />
                </div>
                <div class="flex min-w-0 flex-col">
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('CORTEX.AGENTS.WIZARD.IDENTITY_LOCALES') }}
                  </label>
                  <TagMultiSelectComboBox
                    v-model="selectedAgentAllowedLocales"
                    :options="localeOptions"
                    :placeholder="
                      t(
                        'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.PLACEHOLDER'
                      )
                    "
                    :search-placeholder="
                      t(
                        'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.SEARCH_PLACEHOLDER'
                      )
                    "
                    :empty-state="
                      t(
                        'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.EMPTY'
                      )
                    "
                    :message="t('CORTEX.AGENTS.WIZARD.IDENTITY_LOCALES_HELP')"
                    message-class="sm:min-h-8"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- ═══ STEP 3: Persona ═══ -->
          <div v-if="currentStep === 3">
            <p class="text-sm text-n-slate-11 mb-4">
              {{ t('CORTEX.AGENTS.WIZARD.STEP3_DESC') }}
            </p>
            <div
              v-if="selectedTemplate"
              class="flex items-center gap-2 px-3 py-2 mb-4 rounded-lg bg-n-brand/5 border border-n-brand/20"
            >
              <span class="i-lucide-sparkles size-4 text-n-brand" />
              <span class="text-xs text-n-brand font-medium">
                {{
                  t('CORTEX.AGENTS.WIZARD.TEMPLATE_FROM', {
                    template: selectedTemplate.name,
                  })
                }}
              </span>
            </div>
            <div class="grid gap-5">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('CORTEX.AGENTS.WIZARD.PERSONA_TONE') }}
                </label>
                <p class="mb-3 text-xs text-n-slate-11">
                  {{ t('CORTEX.AGENTS.WIZARD.PERSONA_TONE_HELP') }}
                </p>
                <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <button
                    v-for="toneTrait in toneTraitOptions"
                    :key="toneTrait.value"
                    type="button"
                    class="flex items-center gap-2.5 px-3 py-2.5 text-left border rounded-lg transition-colors"
                    :class="
                      persona.tone_traits.includes(toneTrait.value)
                        ? 'border-n-brand bg-n-brand/10 text-n-brand'
                        : 'border-n-weak text-n-slate-11 hover:border-n-slate-7 hover:bg-n-alpha-1'
                    "
                    :aria-pressed="
                      persona.tone_traits.includes(toneTrait.value)
                    "
                    @click="toggleToneTrait(toneTrait.value)"
                  >
                    <span :class="toneTrait.icon" class="size-4 shrink-0" />
                    <span class="text-sm font-medium">
                      {{ toneTrait.label }}
                    </span>
                    <span
                      v-if="persona.tone_traits.includes(toneTrait.value)"
                      class="i-lucide-check size-3.5 ml-auto shrink-0"
                    />
                  </button>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('CORTEX.AGENTS.WIZARD.PERSONA_TONE_ADDITIONAL') }}
                </label>
                <TextArea
                  v-model="persona.tone"
                  auto-height
                  min-height="5rem"
                  max-height="16rem"
                  :placeholder="
                    t(
                      'CORTEX.AGENTS.WIZARD.PERSONA_TONE_ADDITIONAL_PLACEHOLDER'
                    )
                  "
                />
                <p class="mt-1.5 text-xs text-n-slate-11">
                  {{ t('CORTEX.AGENTS.WIZARD.PERSONA_TONE_ADDITIONAL_HELP') }}
                </p>
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('CORTEX.AGENTS.WIZARD.PERSONA_STYLE') }}
                </label>
                <TextArea
                  v-model="persona.style"
                  auto-height
                  min-height="5rem"
                  max-height="16rem"
                  :placeholder="
                    t('CORTEX.AGENTS.WIZARD.PERSONA_STYLE_PLACEHOLDER')
                  "
                />
                <p class="mt-1.5 text-xs text-n-slate-11">
                  {{ t('CORTEX.AGENTS.WIZARD.PERSONA_STYLE_HELP') }}
                </p>
              </div>

              <div class="pt-1">
                <h3 class="text-sm font-medium text-n-slate-12">
                  {{ t('CORTEX.AGENTS.WIZARD.WRITING_GUIDELINES_TITLE') }}
                </h3>
                <p class="mt-1 text-xs text-n-slate-11">
                  {{ t('CORTEX.AGENTS.WIZARD.WRITING_GUIDELINES_DESC') }}
                </p>
              </div>

              <div class="grid gap-4 sm:grid-cols-2">
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('CORTEX.AGENTS.WIZARD.WRITING_PREFERENCES') }}
                  </label>
                  <TextArea
                    v-model="persona.writing_preferences"
                    auto-height
                    min-height="7rem"
                    max-height="18rem"
                    :placeholder="
                      t('CORTEX.AGENTS.WIZARD.WRITING_PREFERENCES_PLACEHOLDER')
                    "
                  />
                  <p class="mt-1.5 text-xs text-n-slate-11">
                    {{ t('CORTEX.AGENTS.WIZARD.WRITING_PREFERENCES_HELP') }}
                  </p>
                </div>

                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('CORTEX.AGENTS.WIZARD.WRITING_AVOID') }}
                  </label>
                  <TextArea
                    v-model="persona.writing_avoid"
                    auto-height
                    min-height="7rem"
                    max-height="18rem"
                    :placeholder="
                      t('CORTEX.AGENTS.WIZARD.WRITING_AVOID_PLACEHOLDER')
                    "
                  />
                  <p class="mt-1.5 text-xs text-n-slate-11">
                    {{ t('CORTEX.AGENTS.WIZARD.WRITING_AVOID_HELP') }}
                  </p>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('CORTEX.AGENTS.WIZARD.PERSONA_BOUNDARIES') }}
                </label>
                <TextArea
                  v-model="persona.boundaries"
                  auto-height
                  min-height="5rem"
                  max-height="16rem"
                  :placeholder="
                    t('CORTEX.AGENTS.WIZARD.PERSONA_BOUNDARIES_PLACEHOLDER')
                  "
                />
                <p class="mt-1.5 text-xs text-n-slate-11">
                  {{ t('CORTEX.AGENTS.WIZARD.PERSONA_BOUNDARIES_HELP') }}
                </p>
              </div>
            </div>
          </div>

          <!-- ═══ STEP 4: Instructions ═══ -->
          <div v-if="currentStep === 4">
            <p class="text-sm text-n-slate-11 mb-4">
              {{ t('CORTEX.AGENTS.WIZARD.STEP4_DESC') }}
            </p>
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('CORTEX.AGENTS.WIZARD.INSTRUCTIONS_MISSION') }}
                </label>
                <TextArea
                  v-model="instructions.mission"
                  auto-height
                  min-height="10rem"
                  max-height="24rem"
                  :placeholder="
                    t('CORTEX.AGENTS.WIZARD.INSTRUCTIONS_MISSION_PLACEHOLDER')
                  "
                  required
                />
                <p class="mt-1.5 text-xs text-n-slate-11">
                  {{ t('CORTEX.AGENTS.WIZARD.INSTRUCTIONS_MISSION_HELP') }}
                </p>
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('CORTEX.AGENTS.WIZARD.INSTRUCTIONS_SUCCESS') }}
                </label>
                <TextArea
                  v-model="instructions.success_criteria"
                  auto-height
                  min-height="8rem"
                  max-height="20rem"
                  :placeholder="
                    t('CORTEX.AGENTS.WIZARD.INSTRUCTIONS_SUCCESS_PLACEHOLDER')
                  "
                />
                <p class="mt-1.5 text-xs text-n-slate-11">
                  {{ t('CORTEX.AGENTS.WIZARD.INSTRUCTIONS_SUCCESS_HELP') }}
                </p>
              </div>
            </div>
          </div>

          <!-- ═══ STEP 5: Policies ═══ -->
          <div v-if="currentStep === 5">
            <p class="text-sm text-n-slate-11 mb-4">
              {{ t('CORTEX.AGENTS.WIZARD.STEP5_DESC') }}
            </p>
            <div
              class="grid grid-cols-2 gap-1 rounded-lg bg-n-alpha-2 p-1 mb-5"
              role="tablist"
            >
              <NextButton
                type="button"
                role="tab"
                ghost
                slate
                sm
                class="w-full justify-center rounded-md"
                :class="
                  policySection === 'operation'
                    ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                    : 'text-n-slate-10 hover:text-n-slate-12'
                "
                :aria-selected="policySection === 'operation'"
                :label="t('CORTEX.AGENTS.WIZARD.POLICY_SECTION_OPERATION')"
                @click="policySection = 'operation'"
              />
              <NextButton
                type="button"
                role="tab"
                ghost
                slate
                sm
                class="w-full justify-center rounded-md"
                :class="
                  policySection === 'security'
                    ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                    : 'text-n-slate-10 hover:text-n-slate-12'
                "
                :aria-selected="policySection === 'security'"
                :label="t('CORTEX.AGENTS.WIZARD.POLICY_SECTION_SECURITY')"
                @click="policySection = 'security'"
              />
            </div>
            <div v-show="policySection === 'operation'" class="space-y-5">
              <!-- Handoff Policy -->
              <div
                class="p-4 rounded-lg border border-n-weak bg-n-alpha-1 space-y-4"
              >
                <div class="flex items-center gap-2">
                  <span class="i-lucide-arrow-right-left size-4 text-n-brand" />
                  <h3 class="text-sm font-semibold text-n-slate-12">
                    {{ t('CORTEX.AGENTS.WIZARD.HANDOFF_MODE') }}
                  </h3>
                </div>
                <div class="grid gap-4 md:grid-cols-2">
                  <div>
                    <label
                      class="block text-xs font-medium text-n-slate-10 mb-1"
                    >
                      {{ t('CORTEX.AGENTS.WIZARD.HANDOFF_MODE') }}
                    </label>
                    <AdaptiveSelect
                      v-model="handoffPolicy.mode"
                      :options="handoffModeOptions"
                      :aria-label="t('CORTEX.AGENTS.WIZARD.HANDOFF_MODE')"
                      :message="t('CORTEX.AGENTS.WIZARD.HANDOFF_MODE_HELP')"
                      class="w-full"
                    />
                  </div>
                  <div>
                    <label
                      class="block text-xs font-medium text-n-slate-10 mb-1"
                    >
                      {{ t('CORTEX.AGENTS.WIZARD.HANDOFF_LGPD') }}
                    </label>
                    <AdaptiveSelect
                      v-model="handoffPolicy.lgpd_refusal"
                      :options="handoffLgpdOptions"
                      :aria-label="t('CORTEX.AGENTS.WIZARD.HANDOFF_LGPD')"
                      :message="t('CORTEX.AGENTS.WIZARD.HANDOFF_LGPD_HELP')"
                      class="w-full"
                    />
                  </div>
                </div>
              </div>

              <section
                data-testid="cortex-policy-model-resilience"
                class="space-y-4 border-b border-n-weak pb-5"
              >
                <div class="flex items-center gap-2">
                  <span class="i-lucide-brain-circuit size-4 text-n-brand" />
                  <h3 class="text-sm font-semibold text-n-slate-12">
                    {{ t('CORTEX.AGENTS.WIZARD.MODEL_RESILIENCE') }}
                  </h3>
                </div>
                <div>
                  <label
                    class="block mb-1.5 text-sm font-medium text-n-slate-12"
                  >
                    {{ t('CORTEX.AGENTS.WIZARD.FALLBACK_STRATEGY') }}
                  </label>
                  <AdaptiveSelect
                    v-model="modelConfig.fallback_strategy"
                    :aria-label="t('CORTEX.AGENTS.WIZARD.FALLBACK_STRATEGY')"
                    :options="fallbackStrategyOptions"
                    :message="
                      selectedOptionDescription(
                        fallbackStrategyOptions,
                        modelConfig.fallback_strategy
                      )
                    "
                  />
                </div>
                <NextInput
                  v-if="
                    modelConfig.fallback_strategy === 'deterministic_fallback'
                  "
                  v-model="modelConfig.fallback_message"
                  :label="t('CORTEX.AGENTS.WIZARD.FALLBACK_MESSAGE')"
                  :message="t('CORTEX.AGENTS.WIZARD.FALLBACK_MESSAGE_HELP')"
                />
              </section>

              <section
                data-testid="cortex-policy-media-analysis"
                class="space-y-4 border-b border-n-weak pb-5"
              >
                <div class="flex items-center gap-2">
                  <span class="i-lucide-scan-search size-4 text-n-brand" />
                  <h3 class="text-sm font-semibold text-n-slate-12">
                    {{ t('CORTEX.AGENTS.WIZARD.MEDIA_ANALYSIS_POLICY') }}
                  </h3>
                </div>
                <div class="grid gap-4 md:grid-cols-2">
                  <div v-for="field in mediaAnalysisFields" :key="field.key">
                    <label
                      class="block mb-1.5 text-sm font-medium text-n-slate-12"
                    >
                      {{ field.label }}
                    </label>
                    <AdaptiveSelect
                      v-model="mediaAnalysisPolicy[field.key]"
                      :aria-label="field.label"
                      :options="field.options"
                      :message="mediaAnalysisDescription(field)"
                    />
                  </div>
                </div>
              </section>

              <section
                data-testid="cortex-policy-autonomy"
                class="space-y-4 border-b border-n-weak pb-5"
              >
                <div class="flex items-center gap-2">
                  <span class="i-lucide-gauge size-4 text-n-brand" />
                  <h3 class="text-sm font-semibold text-n-slate-12">
                    {{ t('CORTEX.AGENTS.WIZARD.AUTONOMY_MODE') }}
                  </h3>
                </div>
                <AdaptiveSelect
                  v-model="runtimeSettings.autonomy_mode"
                  :aria-label="t('CORTEX.AGENTS.WIZARD.AUTONOMY_MODE')"
                  :options="autonomyModeOptions"
                  :message="
                    selectedOptionDescription(
                      autonomyModeOptions,
                      runtimeSettings.autonomy_mode
                    )
                  "
                />
              </section>

              <section
                data-testid="cortex-policy-context-memory"
                class="space-y-4 border-b border-n-weak pb-5"
              >
                <div class="flex items-center gap-2">
                  <span class="i-lucide-database size-4 text-n-brand" />
                  <h3 class="text-sm font-semibold text-n-slate-12">
                    {{ t('CORTEX.AGENTS.WIZARD.CONTEXT_AND_MEMORY') }}
                  </h3>
                </div>
                <div class="grid gap-4 md:grid-cols-2">
                  <div>
                    <label
                      class="block mb-1.5 text-sm font-medium text-n-slate-12"
                    >
                      {{ t('CORTEX.AGENTS.WIZARD.CONTEXT_BUDGET_PRIORITY') }}
                    </label>
                    <AdaptiveSelect
                      v-model="modelConfig.context_budget_priority"
                      :aria-label="
                        t('CORTEX.AGENTS.WIZARD.CONTEXT_BUDGET_PRIORITY')
                      "
                      :options="contextBudgetOptions"
                      :message="
                        selectedOptionDescription(
                          contextBudgetOptions,
                          modelConfig.context_budget_priority
                        )
                      "
                    />
                  </div>
                  <NextInput
                    v-model="modelConfig.context_budget_tokens"
                    type="number"
                    :label="t('CORTEX.AGENTS.WIZARD.CONTEXT_BUDGET_TOKENS')"
                    :message="
                      t('CORTEX.AGENTS.WIZARD.CONTEXT_BUDGET_TOKENS_HELP')
                    "
                  />
                  <div>
                    <label
                      class="block mb-1.5 text-sm font-medium text-n-slate-12"
                    >
                      {{ t('CORTEX.AGENTS.WIZARD.MEMORY_CONSOLIDATION_MODE') }}
                    </label>
                    <AdaptiveSelect
                      v-model="
                        runtimeSettings.contact_profile_consolidation_mode
                      "
                      :aria-label="
                        t('CORTEX.AGENTS.WIZARD.MEMORY_CONSOLIDATION_MODE')
                      "
                      :options="consolidationModeOptions"
                      :message="
                        selectedOptionDescription(
                          consolidationModeOptions,
                          runtimeSettings.contact_profile_consolidation_mode
                        )
                      "
                    />
                  </div>
                  <NextInput
                    v-model="runtimeSettings.rolling_summary_message_threshold"
                    type="number"
                    :label="t('CORTEX.AGENTS.WIZARD.ROLLING_SUMMARY_THRESHOLD')"
                    :message="
                      t('CORTEX.AGENTS.WIZARD.ROLLING_SUMMARY_THRESHOLD_HELP')
                    "
                  />
                  <div class="md:col-span-2 md:grid md:grid-cols-2 md:gap-4">
                    <NextInput
                      v-model="runtimeSettings.rolling_summary_frequency"
                      type="number"
                      :label="
                        t('CORTEX.AGENTS.WIZARD.ROLLING_SUMMARY_FREQUENCY')
                      "
                      :message="
                        t('CORTEX.AGENTS.WIZARD.ROLLING_SUMMARY_FREQUENCY_HELP')
                      "
                    />
                  </div>
                </div>
              </section>
            </div>

            <!-- Safety Policy -->
            <div v-show="policySection === 'security'" class="space-y-3">
              <div class="flex items-center gap-2">
                <span class="i-lucide-shield-check size-4 text-n-brand" />
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ t('CORTEX.AGENTS.CONFIG_SECTIONS.SAFETY_POLICY') }}
                </h3>
              </div>
              <label class="flex items-start gap-2.5 cursor-pointer">
                <NextCheckbox
                  v-model="safetyPolicy.allow_general_model_knowledge"
                  class="mt-0.5"
                />
                <span class="text-sm text-n-slate-12">
                  {{ t('CORTEX.AGENTS.WIZARD.SAFETY_GENERAL_KNOWLEDGE') }}
                </span>
              </label>
              <label class="flex items-start gap-2.5 cursor-pointer">
                <NextCheckbox
                  v-model="safetyPolicy.require_source_for_factual_answers"
                  class="mt-0.5"
                />
                <span class="text-sm text-n-slate-12">
                  {{ t('CORTEX.AGENTS.WIZARD.SAFETY_REQUIRE_SOURCE') }}
                </span>
              </label>
              <div class="grid gap-4 md:grid-cols-2 pt-2">
                <label class="flex items-start gap-2.5 cursor-pointer">
                  <NextCheckbox
                    v-model="safetyPolicy.prompt_injection_protection_enabled"
                    class="mt-0.5"
                  />
                  <span class="text-sm text-n-slate-12">
                    {{ t('CORTEX.AGENTS.WIZARD.SAFETY_PROMPT_INJECTION') }}
                  </span>
                </label>
                <label class="flex items-start gap-2.5 cursor-pointer">
                  <NextCheckbox
                    v-model="safetyPolicy.sensitive_data_detection_enabled"
                    class="mt-0.5"
                  />
                  <span class="text-sm text-n-slate-12">
                    {{ t('CORTEX.AGENTS.WIZARD.SAFETY_SENSITIVE_DATA') }}
                  </span>
                </label>
              </div>
              <div class="grid gap-4 md:grid-cols-2">
                <div>
                  <label class="block text-xs font-medium text-n-slate-10 mb-1">
                    {{ t('CORTEX.AGENTS.WIZARD.GUARDRAIL_DEFAULT_ACTION') }}
                  </label>
                  <AdaptiveSelect
                    v-model="safetyPolicy.guardrail_default_action"
                    :options="guardrailActionOptions"
                    :message="
                      t('CORTEX.AGENTS.WIZARD.GUARDRAIL_DEFAULT_ACTION_HELP')
                    "
                    class="w-full"
                  />
                </div>
                <div>
                  <label class="block text-xs font-medium text-n-slate-10 mb-1">
                    {{ t('CORTEX.AGENTS.WIZARD.SENSITIVE_DATA_ACTION') }}
                  </label>
                  <AdaptiveSelect
                    v-model="safetyPolicy.sensitive_data_action"
                    :options="sensitiveDataActionOptions"
                    :message="
                      t('CORTEX.AGENTS.WIZARD.SENSITIVE_DATA_ACTION_HELP')
                    "
                    class="w-full"
                  />
                </div>
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-10 mb-2">
                  {{ t('CORTEX.AGENTS.WIZARD.SENSITIVE_DATA_CATEGORIES') }}
                </label>
                <div class="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <label
                    v-for="category in sensitiveDataCategoryOptions"
                    :key="category.value"
                    class="flex items-center gap-2 text-sm text-n-slate-12"
                  >
                    <NextCheckbox
                      :model-value="
                        safetyPolicy.sensitive_data_categories.includes(
                          category.value
                        )
                      "
                      @update:model-value="
                        toggleSensitiveDataCategory(category.value)
                      "
                    />
                    <span>{{ category.label }}</span>
                  </label>
                </div>
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-10 mb-1">
                  {{ t('CORTEX.AGENTS.WIZARD.BLOCKED_TOPICS') }}
                </label>
                <TextArea
                  v-model="blockedTopicsText"
                  auto-height
                  min-height="5rem"
                  max-height="16rem"
                  :placeholder="
                    t('CORTEX.AGENTS.WIZARD.BLOCKED_TOPICS_PLACEHOLDER')
                  "
                />
                <p class="mt-1.5 text-xs text-n-slate-11">
                  {{ t('CORTEX.AGENTS.WIZARD.BLOCKED_TOPICS_HELP') }}
                </p>
              </div>
            </div>
          </div>

          <!-- ═══ STEP 6: Scenarios ═══ -->
          <div v-if="currentStep === 6">
            <p class="text-sm text-n-slate-11 mb-1">
              {{ t('CORTEX.AGENTS.WIZARD.STEP6_DESC') }}
            </p>
            <p class="text-xs text-n-slate-11 mb-4">
              {{ t('CORTEX.AGENTS.WIZARD.STEP6_OPTIONAL') }}
            </p>
            <div class="space-y-3 mb-4">
              <div
                v-for="(scenario, idx) in wizardScenarios"
                :key="scenario.id"
                class="px-3 py-3 rounded-lg border border-n-weak/50 bg-n-alpha-1"
              >
                <div v-if="editingScenarioId === scenario.id" class="space-y-3">
                  <div
                    class="grid grid-cols-1 items-start gap-3 sm:grid-cols-[minmax(0,1fr)_9rem]"
                  >
                    <NextInput
                      v-model="scenarioDraft.name"
                      :label="t('CORTEX.AGENTS.WIZARD.SCENARIO_NAME')"
                      :message="t('CORTEX.AGENTS.WIZARD.SCENARIO_NAME_HELP')"
                    />
                    <NextInput
                      :id="`cortex-scenario-priority-${scenario.id}`"
                      v-model.number="scenarioDraft.priority"
                      type="number"
                      :label="t('CORTEX.AGENTS.WIZARD.SCENARIO_PRIORITY')"
                      :hint="t('CORTEX.AGENTS.WIZARD.SCENARIO_PRIORITY_HELP')"
                    />
                  </div>
                  <TextArea
                    v-model="scenarioDraft.mission"
                    :placeholder="t('CORTEX.AGENTS.WIZARD.SCENARIO_MISSION')"
                    :label="t('CORTEX.AGENTS.WIZARD.SCENARIO_MISSION')"
                    :message="t('CORTEX.AGENTS.WIZARD.SCENARIO_MISSION_HELP')"
                    :max-length="5000"
                    min-height="5rem"
                  />
                  <TextArea
                    v-model="scenarioDraft.matchAnyText"
                    :label="t('CORTEX.AGENTS.WIZARD.SCENARIO_MATCH_CRITERIA')"
                    :placeholder="
                      t(
                        'CORTEX.AGENTS.WIZARD.SCENARIO_MATCH_CRITERIA_PLACEHOLDER'
                      )
                    "
                    :message="
                      t('CORTEX.AGENTS.WIZARD.SCENARIO_MATCH_CRITERIA_HELP')
                    "
                    :max-length="2000"
                    min-height="5rem"
                  />
                  <div class="flex justify-end gap-2">
                    <NextButton
                      type="button"
                      slate
                      outline
                      sm
                      :label="t('CORTEX.AGENTS.WIZARD.CANCEL_SCENARIO_EDIT')"
                      @click="cancelScenarioEdit"
                    />
                    <NextButton
                      type="button"
                      sm
                      :disabled="!scenarioDraft.name.trim()"
                      :label="t('CORTEX.AGENTS.WIZARD.SAVE_SCENARIO')"
                      @click="saveScenarioEdit(scenario)"
                    />
                  </div>
                </div>
                <div v-else class="flex items-start gap-3 group">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="text-sm font-medium text-n-slate-12">
                        {{ scenario.name }}
                      </span>
                      <span
                        class="text-[10px] px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-10"
                      >
                        {{
                          t('CORTEX.AGENTS.WIZARD.PRIORITY_SHORT', {
                            priority: scenario.priority,
                          })
                        }}
                      </span>
                      <button
                        type="button"
                        class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                        :class="scenarioStatusMeta(scenario).className"
                        :aria-label="scenarioStatusMeta(scenario).actionLabel"
                        :title="scenarioStatusMeta(scenario).actionLabel"
                        @click="toggleScenarioStatus(scenario)"
                      >
                        <span
                          :class="scenarioStatusMeta(scenario).icon"
                          class="size-3"
                        />
                        {{ scenarioStatusMeta(scenario).label }}
                      </button>
                    </div>
                    <p
                      v-if="scenario.description"
                      class="text-xs text-n-slate-11 mt-1 truncate"
                    >
                      {{ scenario.description }}
                    </p>
                  </div>
                  <NextButton
                    type="button"
                    ghost
                    slate
                    xs
                    icon="i-lucide-pencil"
                    :aria-label="t('CORTEX.AGENTS.WIZARD.EDIT_SCENARIO')"
                    :title="t('CORTEX.AGENTS.WIZARD.EDIT_SCENARIO')"
                    @click="editScenario(scenario)"
                  />
                  <NextButton
                    type="button"
                    ghost
                    ruby
                    xs
                    icon="i-lucide-trash-2"
                    :aria-label="t('CORTEX.AGENTS.WIZARD.REMOVE_SCENARIO')"
                    :title="t('CORTEX.AGENTS.WIZARD.REMOVE_SCENARIO')"
                    @click="removeScenario(idx)"
                  />
                </div>
              </div>
              <p
                v-if="!wizardScenarios.length"
                class="text-sm text-n-slate-10 py-4 text-center"
              >
                {{ t('CORTEX.AGENTS.WIZARD.NO_SCENARIOS') }}
              </p>
            </div>

            <!-- Add scenario form -->
            <div
              class="p-4 space-y-3 border border-dashed rounded-lg border-n-weak bg-n-surface-2"
            >
              <h4 class="text-xs font-semibold text-n-slate-11 uppercase">
                {{ t('CORTEX.AGENTS.WIZARD.ADD_SCENARIO') }}
              </h4>
              <p class="text-xs text-n-slate-11">
                {{ t('CORTEX.AGENTS.WIZARD.SCENARIO_FIELDS_HELP') }}
              </p>
              <div
                data-testid="cortex-new-scenario-main-fields"
                class="grid grid-cols-1 items-start gap-3 sm:grid-cols-[minmax(0,1fr)_9rem]"
              >
                <NextInput
                  v-model="newScenario.name"
                  class="w-full"
                  :label="t('CORTEX.AGENTS.WIZARD.SCENARIO_NAME')"
                  :placeholder="t('CORTEX.AGENTS.WIZARD.SCENARIO_NAME')"
                  :message="t('CORTEX.AGENTS.WIZARD.SCENARIO_NAME_HELP')"
                />
                <NextInput
                  id="cortex-new-scenario-priority"
                  v-model.number="newScenario.priority"
                  type="number"
                  class="w-full"
                  :label="t('CORTEX.AGENTS.WIZARD.SCENARIO_PRIORITY')"
                  :placeholder="t('CORTEX.AGENTS.WIZARD.SCENARIO_PRIORITY')"
                  :hint="t('CORTEX.AGENTS.WIZARD.SCENARIO_PRIORITY_HELP')"
                />
              </div>
              <TextArea
                v-model="newScenario.mission"
                :placeholder="t('CORTEX.AGENTS.WIZARD.SCENARIO_MISSION')"
                :message="t('CORTEX.AGENTS.WIZARD.SCENARIO_MISSION_HELP')"
                :max-length="5000"
                min-height="4rem"
              />
              <TextArea
                v-model="newScenario.matchAnyText"
                :label="t('CORTEX.AGENTS.WIZARD.SCENARIO_MATCH_CRITERIA')"
                :placeholder="
                  t('CORTEX.AGENTS.WIZARD.SCENARIO_MATCH_CRITERIA_PLACEHOLDER')
                "
                :message="
                  t('CORTEX.AGENTS.WIZARD.SCENARIO_MATCH_CRITERIA_HELP')
                "
                :max-length="2000"
                min-height="5rem"
              />
              <NextButton
                type="button"
                blue
                outline
                sm
                :disabled="!newScenario.name.trim()"
                @click="addScenario"
              >
                <span class="i-lucide-plus size-4" />
                {{ t('CORTEX.AGENTS.WIZARD.ADD_SCENARIO') }}
              </NextButton>
            </div>
          </div>

          <!-- ═══ STEP 7: Tools ═══ -->
          <div v-if="currentStep === 7">
            <p class="text-sm text-n-slate-11 mb-4">
              {{ t('CORTEX.AGENTS.WIZARD.STEP7_DESC') }}
            </p>

            <!-- Existing tools list -->
            <div v-if="activeTools.length > 0" class="space-y-2 mb-4">
              <button
                v-for="tool in activeTools"
                :key="tool.id"
                type="button"
                class="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border transition-colors text-left"
                :class="
                  selectedToolIds.includes(tool.id)
                    ? 'border-n-brand/30 bg-n-brand/5'
                    : 'border-n-weak/50 bg-n-alpha-1 hover:border-n-slate-8'
                "
                @click="toggleTool(tool.id)"
              >
                <div class="flex items-center gap-3 min-w-0">
                  <span
                    class="i-lucide-wrench size-4 shrink-0"
                    :class="
                      selectedToolIds.includes(tool.id)
                        ? 'text-n-brand'
                        : 'text-n-slate-10'
                    "
                  />
                  <div class="min-w-0">
                    <span class="text-sm font-medium text-n-slate-12 block">
                      {{ tool.name }}
                    </span>
                    <span
                      v-if="tool.description"
                      class="text-xs text-n-slate-11 truncate block"
                    >
                      {{ tool.description }}
                    </span>
                  </div>
                </div>
                <div class="flex items-center gap-2 shrink-0">
                  <Label
                    :label="riskBadgeLabel(tool.risk_level)"
                    :color="riskBadgeColor(tool.risk_level)"
                    compact
                  />
                  <span
                    class="inline-flex items-center gap-1 text-[10px] font-medium"
                    :class="
                      selectedToolIds.includes(tool.id)
                        ? 'text-n-brand'
                        : 'text-n-slate-10'
                    "
                  >
                    <span
                      :class="
                        selectedToolIds.includes(tool.id)
                          ? 'i-lucide-circle-check'
                          : 'i-lucide-circle-minus'
                      "
                      class="size-3.5"
                    />
                    {{
                      selectedToolIds.includes(tool.id)
                        ? t('CORTEX.AGENTS.WIZARD.TOOL_CONNECTED')
                        : t('CORTEX.AGENTS.WIZARD.TOOL_NOT_CONNECTED')
                    }}
                  </span>
                </div>
              </button>
            </div>
            <p
              v-else-if="!showToolForm"
              class="text-sm text-n-slate-10 py-4 text-center"
            >
              {{ t('CORTEX.AGENTS.WIZARD.NO_TOOLS') }}
            </p>

            <!-- Create new tool -->
            <div class="mt-3">
              <button
                v-if="!showToolForm"
                type="button"
                class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-n-brand border border-dashed border-n-brand/30 rounded-lg hover:bg-n-brand/5 transition-colors w-full justify-center"
                @click="showToolForm = true"
              >
                <span class="i-lucide-plus size-4" />
                {{ t('CORTEX.AGENTS.WIZARD.TOOL_CREATE_NEW') }}
              </button>
              <CortexToolInlineForm
                v-else
                @created="handleToolCreated"
                @cancel="showToolForm = false"
              />
            </div>
          </div>

          <!-- ═══ STEP 8: Inboxes ═══ -->
          <div v-if="currentStep === 8">
            <div class="grid gap-5 mb-6">
              <WithLabel
                name="cortex-agent-wizard-status"
                :label="t('CORTEX.AGENTS.CONFIG.STATUS')"
                :help-message="t('CORTEX.AGENTS.WIZARD.AGENT_STATUS_HELP')"
              >
                <AdaptiveSelect
                  v-model="agentStatus"
                  :options="agentStatusOptions"
                  class="w-full"
                />
              </WithLabel>

              <fieldset class="grid gap-2">
                <legend class="mb-1 text-sm font-medium text-n-slate-12">
                  {{ t('CORTEX.AGENTS.CONFIG.KNOWLEDGE_BASES') }}
                </legend>
                <button
                  v-for="knowledgeBase in knowledgeBases"
                  :key="knowledgeBase.id"
                  type="button"
                  class="flex items-start gap-3 p-3 text-left border rounded-lg transition-colors"
                  :class="
                    allowedKnowledgeBaseIds.includes(knowledgeBase.id)
                      ? 'border-n-brand bg-n-brand/10'
                      : 'border-n-weak hover:bg-n-alpha-2'
                  "
                  @click="toggleKnowledgeBase(knowledgeBase.id)"
                >
                  <NextCheckbox
                    :model-value="
                      allowedKnowledgeBaseIds.includes(knowledgeBase.id)
                    "
                    class="mt-0.5 pointer-events-none"
                  />
                  <span class="min-w-0">
                    <span class="block text-sm font-medium text-n-slate-12">
                      {{ knowledgeBase.name }}
                    </span>
                    <span class="block mt-1 text-xs text-n-slate-11">
                      {{
                        knowledgeBase.description ||
                        t('CORTEX.KNOWLEDGE.NO_DESCRIPTION')
                      }}
                    </span>
                  </span>
                </button>
                <p
                  v-if="!knowledgeBases.length"
                  class="text-sm text-n-slate-11"
                >
                  {{ t('CORTEX.AGENTS.CONFIG.NO_KNOWLEDGE_BASES') }}
                </p>
              </fieldset>
            </div>

            <p class="text-sm text-n-slate-11 mb-4">
              {{ t('CORTEX.AGENTS.WIZARD.STEP8_DESC') }}
            </p>
            <div
              v-if="allInboxes && allInboxes.length > 0"
              class="flex flex-wrap gap-2 p-3 rounded-lg border border-n-weak bg-n-surface-2 min-h-[60px]"
            >
              <button
                v-for="inbox in allInboxes"
                :key="inbox.id"
                type="button"
                class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150 border"
                :class="
                  wizardInboxIds.includes(inbox.id)
                    ? 'bg-n-brand/10 border-n-brand text-n-brand dark:bg-n-brand/20'
                    : 'bg-n-surface-1 border-n-weak text-n-slate-10 hover:border-n-slate-8'
                "
                @click="toggleWizardInbox(inbox.id)"
              >
                <span
                  class="size-2 rounded-full shrink-0"
                  :class="
                    wizardInboxIds.includes(inbox.id)
                      ? 'bg-current'
                      : 'bg-n-slate-8'
                  "
                />
                {{ inbox.name }}
                <span
                  v-if="wizardInboxIds.includes(inbox.id)"
                  class="i-lucide-check size-3.5"
                />
              </button>
            </div>
            <div
              v-else
              class="flex items-center justify-center py-8 text-sm border border-dashed rounded-lg text-n-slate-9 border-n-weak"
            >
              {{ t('CORTEX.AGENTS.WIZARD.NO_INBOXES') }}
            </div>
            <p class="text-[11px] text-n-slate-11 mt-2">
              {{ t('CORTEX.AGENTS.WIZARD.INBOXES_HELP') }}
            </p>
            <div v-if="selectedWizardInboxes.length" class="mt-5 space-y-3">
              <div
                v-for="inbox in selectedWizardInboxes"
                :key="`settings-${inbox.id}`"
                class="p-4 border rounded-lg border-n-weak bg-n-alpha-1"
              >
                <p class="text-sm font-medium text-n-slate-12">
                  {{ inbox.name }}
                </p>
                <p class="mt-1 text-xs text-n-slate-11">
                  {{ t('CORTEX.AGENTS.WIZARD.INBOX_OPERATIONAL_MODE_HELP') }}
                </p>
                <div class="mt-3 grid gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    class="rounded-lg border p-3 text-left transition-colors"
                    :class="
                      ensureInboxSettings(inbox.id).operational_mode ===
                      'observe_only'
                        ? 'border-n-brand bg-n-brand/10'
                        : 'border-n-weak hover:border-n-slate-7'
                    "
                    @click="
                      ensureInboxSettings(inbox.id).operational_mode =
                        'observe_only'
                    "
                  >
                    <span class="block text-sm font-medium text-n-slate-12">
                      {{ t('CORTEX.AGENTS.WIZARD.INBOX_MODE_OBSERVE_ONLY') }}
                    </span>
                    <span class="mt-1 block text-xs text-n-slate-11">
                      {{
                        t('CORTEX.AGENTS.WIZARD.INBOX_MODE_OBSERVE_ONLY_HELP')
                      }}
                    </span>
                  </button>
                  <button
                    type="button"
                    class="rounded-lg border p-3 text-left transition-colors"
                    :class="
                      ensureInboxSettings(inbox.id).operational_mode ===
                      'respond'
                        ? 'border-n-brand bg-n-brand/10'
                        : 'border-n-weak hover:border-n-slate-7'
                    "
                    @click="
                      ensureInboxSettings(inbox.id).operational_mode = 'respond'
                    "
                  >
                    <span class="block text-sm font-medium text-n-slate-12">
                      {{ t('CORTEX.AGENTS.WIZARD.INBOX_MODE_RESPOND') }}
                    </span>
                    <span class="mt-1 block text-xs text-n-slate-11">
                      {{ t('CORTEX.AGENTS.WIZARD.INBOX_MODE_RESPOND_HELP') }}
                    </span>
                  </button>
                </div>
                <label
                  class="mt-4 flex items-start gap-3 rounded-lg border border-n-weak p-3 text-sm text-n-slate-12"
                >
                  <NextCheckbox
                    v-model="
                      ensureInboxSettings(inbox.id)
                        .cortex_chat_external_visual_enabled
                    "
                    class="mt-0.5"
                  />
                  <span>
                    <span class="block font-medium">{{
                      t(
                        'CORTEX.AGENTS.WIZARD.CORTEX_CHAT_EXTERNAL_VISUAL_ENABLED'
                      )
                    }}</span>
                    <span class="mt-1 block text-xs text-n-slate-11">{{
                      t(
                        'CORTEX.AGENTS.WIZARD.CORTEX_CHAT_EXTERNAL_VISUAL_ENABLED_HELP'
                      )
                    }}</span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <!-- ═══ STEP 9: Confirmation ═══ -->
          <div v-if="currentStep === 9">
            <p class="text-sm text-n-slate-11 mb-4">
              {{ t('CORTEX.AGENTS.WIZARD.STEP9_DESC') }}
            </p>
            <div class="space-y-5">
              <!-- Identity -->
              <div>
                <label
                  class="block text-xs font-medium text-n-slate-10 uppercase tracking-wider mb-2"
                >
                  {{ t('CORTEX.AGENTS.WIZARD.STEP_IDENTITY') }}
                </label>
                <div class="flex items-center gap-3">
                  <div
                    v-if="selectedTemplate"
                    class="flex items-center justify-center size-10 rounded-lg shrink-0"
                    :class="templateColorClass(selectedTemplate.key)"
                  >
                    <span
                      :class="
                        TEMPLATE_ICONS[selectedTemplate.key] || 'i-lucide-bot'
                      "
                      class="size-5"
                    />
                  </div>
                  <div>
                    <h3 class="text-sm font-semibold text-n-slate-12">
                      {{ agentName }}
                    </h3>
                    <p
                      v-if="agentDescription"
                      class="text-xs text-n-slate-11 mt-0.5"
                    >
                      {{ agentDescription }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Persona -->
              <div>
                <label
                  class="block text-xs font-medium text-n-slate-10 uppercase tracking-wider mb-2"
                >
                  {{ t('CORTEX.AGENTS.WIZARD.STEP_PERSONA') }}
                </label>
                <div class="grid gap-2">
                  <div
                    v-if="selectedToneTraitLabels.length"
                    class="flex flex-wrap gap-1.5"
                  >
                    <Label
                      v-for="toneTrait in selectedToneTraitLabels"
                      :key="toneTrait"
                      :label="toneTrait"
                      compact
                    />
                  </div>
                  <span
                    v-if="persona.tone"
                    class="text-sm font-medium text-n-slate-12"
                  >
                    {{ persona.tone }}
                  </span>
                  <p
                    v-if="persona.style"
                    class="text-sm text-n-slate-11 line-clamp-2"
                  >
                    {{ persona.style }}
                  </p>
                  <p
                    v-if="persona.writing_preferences"
                    class="text-xs text-n-slate-11 line-clamp-2"
                  >
                    <span class="font-medium text-n-slate-11">
                      {{ t('CORTEX.AGENTS.WIZARD.WRITING_PREFERENCES') }}
                    </span>
                    {{ persona.writing_preferences }}
                  </p>
                  <p
                    v-if="persona.writing_avoid"
                    class="text-xs text-n-slate-11 line-clamp-2"
                  >
                    <span class="font-medium text-n-slate-11">
                      {{ t('CORTEX.AGENTS.WIZARD.WRITING_AVOID') }}
                    </span>
                    {{ persona.writing_avoid }}
                  </p>
                </div>
              </div>

              <!-- Instructions -->
              <div v-if="instructions.mission">
                <label
                  class="block text-xs font-medium text-n-slate-10 uppercase tracking-wider mb-2"
                >
                  {{ t('CORTEX.AGENTS.WIZARD.STEP_INSTRUCTIONS') }}
                </label>
                <p class="text-sm text-n-slate-12 line-clamp-2">
                  {{ instructions.mission }}
                </p>
              </div>

              <!-- Policies -->
              <div>
                <label
                  class="block text-xs font-medium text-n-slate-10 uppercase tracking-wider mb-2"
                >
                  {{ t('CORTEX.AGENTS.WIZARD.STEP_POLICIES') }}
                </label>
                <div class="flex flex-wrap gap-1.5">
                  <Label
                    :label="
                      t('CORTEX.AGENTS.WIZARD.HANDOFF_SUMMARY', {
                        mode: handoffModeSummaryLabel,
                      })
                    "
                    compact
                  />
                  <Label
                    :label="
                      t('CORTEX.AGENTS.WIZARD.HANDOFF_LGPD_SUMMARY', {
                        action: handoffLgpdSummaryLabel,
                      })
                    "
                    compact
                  />
                </div>
              </div>

              <!-- Scenarios -->
              <div v-if="wizardScenarios.length > 0">
                <label
                  class="block text-xs font-medium text-n-slate-10 uppercase tracking-wider mb-2"
                >
                  {{
                    t('CORTEX.AGENTS.WIZARD.CONFIRM_SCENARIOS', {
                      count: wizardScenarios.length,
                    })
                  }}
                </label>
                <div class="flex flex-wrap gap-1.5">
                  <Label
                    v-for="scenario in wizardScenarios"
                    :key="scenario.id"
                    :label="scenario.name"
                    compact
                  >
                    <template #icon>
                      <span class="i-lucide-git-branch size-3" />
                    </template>
                  </Label>
                </div>
              </div>

              <!-- Tools -->
              <div v-if="selectedActiveToolIds.length > 0">
                <label
                  class="block text-xs font-medium text-n-slate-10 uppercase tracking-wider mb-2"
                >
                  {{
                    t('CORTEX.AGENTS.WIZARD.CONFIRM_TOOLS', {
                      count: selectedActiveToolIds.length,
                    })
                  }}
                </label>
                <div class="flex flex-wrap gap-1.5">
                  <Label
                    v-for="toolId in selectedActiveToolIds"
                    :key="toolId"
                    :label="allTools.find(t => t.id === toolId)?.name || toolId"
                    compact
                  >
                    <template #icon>
                      <span class="i-lucide-wrench size-3" />
                    </template>
                  </Label>
                </div>
              </div>

              <!-- Inboxes -->
              <div v-if="wizardInboxIds.length > 0">
                <label
                  class="block text-xs font-medium text-n-slate-10 uppercase tracking-wider mb-2"
                >
                  {{
                    t('CORTEX.AGENTS.WIZARD.CONFIRM_INBOXES', {
                      count: wizardInboxIds.length,
                    })
                  }}
                </label>
                <div class="flex flex-wrap gap-1.5">
                  <Label
                    v-for="inboxId in wizardInboxIds"
                    :key="inboxId"
                    :label="
                      allInboxes.find(i => i.id === inboxId)?.name || inboxId
                    "
                    color="brand"
                    compact
                  >
                    <template #icon>
                      <span class="i-lucide-inbox size-3" />
                    </template>
                  </Label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div
          class="flex items-center justify-between px-6 py-4 border-t border-n-weak shrink-0 bg-n-alpha-1"
        >
          <button
            type="button"
            class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-n-slate-11 rounded-lg hover:bg-n-alpha-2 transition-colors"
            @click="goBack"
          >
            <span class="i-lucide-arrow-left size-4" />
            {{
              currentStep === (isEditMode ? 2 : 1)
                ? t('CORTEX.AGENTS.WIZARD.BTN_CANCEL')
                : t('CORTEX.AGENTS.WIZARD.BTN_BACK')
            }}
          </button>

          <div class="flex items-center gap-2">
            <!-- Skip (steps 6, 7, 8) -->
            <button
              v-if="currentStep >= 6 && currentStep <= 8"
              type="button"
              class="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-n-slate-11 rounded-lg hover:bg-n-alpha-2 transition-colors"
              @click="currentStep++"
            >
              <span class="i-lucide-skip-forward size-4" />
              {{ t('CORTEX.AGENTS.WIZARD.BTN_SKIP') }}
            </button>

            <!-- Next -->
            <button
              v-if="currentStep >= 2 && currentStep <= 8"
              type="button"
              class="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-n-brand rounded-lg hover:bg-n-brand/90 transition-colors disabled:opacity-50"
              :disabled="!canAdvance"
              @click="goNext"
            >
              {{ t('CORTEX.AGENTS.WIZARD.BTN_NEXT') }}
              <span class="i-lucide-arrow-right size-4" />
            </button>

            <!-- Create / Save -->
            <button
              v-if="currentStep === 9"
              type="button"
              :disabled="!canCreate || isCreating"
              class="flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white bg-n-brand rounded-lg hover:bg-n-brand/90 transition-colors shadow-sm disabled:opacity-50"
              @click="createOrUpdateAgent"
            >
              <span
                v-if="isCreating"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              <span
                v-else
                :class="isEditMode ? 'i-lucide-save' : 'i-lucide-wand-sparkles'"
                class="size-4"
              />
              {{
                isEditMode
                  ? t('CORTEX.AGENTS.WIZARD.BTN_SAVE')
                  : t('CORTEX.AGENTS.WIZARD.BTN_CREATE')
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
